import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wind,
  Volume2,
  Zap,
  LayoutGrid,
  Star,
  ShieldCheck,
  Truck,
  RotateCcw,
  Wallet,
  CheckCircle2,
  MessageCircle,
  ShoppingCart,
  Flame,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Placeholder } from "../components/Placeholder";
import Slideshow from "../components/Slideshow";
import NotifyMeButton from "../components/NotifyMeButton";
import HorizontalCarousel from "../components/HorizontalCarousel";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { fetchProduct } from "../lib/api";
import { toast } from "sonner";
import {
  BRAND,
  PRICING,
  PRODUCT_FEATURES,
  TECH_SPECS,
  COMPARISONS,
  FAQS,
  TRUST_BADGES,
} from "../data/content";
import { REVIEWS } from "../data/reviews";

const FEATURE_ICONS = [Wind, LayoutGrid, Volume2, Zap, ShieldCheck, RotateCcw];
const TRUST_ICONS = { Truck, RotateCcw, Wallet, ShieldCheck };

const ASSETS = "https://customer-assets.emergentagent.com/job_gadget-store-179/artifacts";
const ASSETS_V2 = "https://customer-assets-lqy194kg.emergentagent.net/job_gadget-store-179/artifacts";
const HERO_VIDEO = `${ASSETS_V2}/w26y7cqa_Tower_fan_oscillating_on_counter_202607271928.mp4`;
const PRODUCT_PHOTOS = [
  { url: `${ASSETS}/bmbrgm3t_01_Lifestyle_Kitchen.png`, alt: "Gruham fan in a real kitchen" },
  { url: `${ASSETS_V2}/82dvljur_2.png`, alt: "Focused flame — does not affect the gas flame" },
  { url: `${ASSETS_V2}/5zzh9lvc_3.png`, alt: "Powerful airflow, 90° oscillation, 3 speeds, low noise" },
  { url: `${ASSETS}/qe0qfopj_02_Control_Dial.png`, alt: "Easy control dial detail" },
  { url: `${ASSETS}/b12bkbhb_04_Oscillation.png`, alt: "Oscillation coverage" },
  { url: `${ASSETS}/povo8ixk_05_Height.png`, alt: "29-inch ideal height" },
];

// Client-provided side-by-side comparison images.
const COMPARISON_IMAGES = [
  `${ASSETS_V2}/wsvb0ktd_comparison%201.png`,
  `${ASSETS_V2}/7n0qrnzd_comparision%202.png`,
  `${ASSETS_V2}/zou953u5_comparision%203.png`,
];

// Reusable inline CTA (repeated across the page)
const InlineCTA = ({ onAdd, testId }) => {
  const { t } = useI18n();
  return (
    <div className="mt-10 flex justify-center">
      <Button
        data-testid={testId}
        onClick={onAdd}
        className="cta-red rounded-full h-13 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-semibold inline-flex items-center gap-2"
      >
        <ShoppingCart className="w-4 h-4" />
        {t("add_to_cart")}
      </Button>
    </div>
  );
};

const Home = () => {
  const { addItem, setOpen } = useCart();
  const { t, lang } = useI18n();
  const [product, setProduct] = useState(null);
  const heroVideoRef = useRef(null);

  useEffect(() => {
    fetchProduct().then(setProduct).catch(() => {});
  }, []);

  // Reliable hero-video autoplay: some browsers (esp. mobile Safari, throttled
  // tabs) drop the initial autoplay attempt on a hard refresh. We force a play()
  // call once metadata is ready and again if the tab becomes visible.
  useEffect(() => {
    const el = heroVideoRef.current;
    if (!el) return;
    // Ensure attributes are set in JS too (Safari sometimes ignores JSX order)
    el.muted = true;
    el.defaultMuted = true;
    el.playsInline = true;

    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          /* autoplay policy blocked — will retry on next event */
        });
      }
    };
    tryPlay();
    const onCanPlay = () => tryPlay();
    const onLoaded = () => tryPlay();
    const onVisibility = () => {
      if (document.visibilityState === "visible") tryPlay();
    };
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("loadedmetadata", onLoaded);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("loadedmetadata", onLoaded);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const allReviews = REVIEWS;

  const productId = product?.id || "mothers-care-kitchen-cool-fan";
  const productName = product?.name || "Kitchen Cool Fan";
  const price = product?.price ?? PRICING.price;
  const mrp = product?.mrp ?? PRICING.mrp;
  const rating = product?.rating ?? 4.4;
  const reviewsCount = product?.reviews_count ?? 127;
  const discountPercent = Math.round(((mrp - price) / mrp) * 100);
  const outOfStock = product ? (product.stock ?? 0) <= 0 : false;

  const handleAddToCart = () => {
    addItem({ id: productId, name: productName, price });
    toast.success(lang === "gu" ? "કાર્ટમાં ઉમેરાયું" : "Added to cart", {
      description: productName,
    });
    setOpen(true);
  };

  const scrollToReviews = () => {
    const el = document.getElementById("reviews");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const waLink = `https://wa.me/${BRAND.phoneDigits}?text=${encodeURIComponent(
    BRAND.whatsappMessage
  )}`;

  // 4 real demo videos supplied by client — autoplay muted loop
  const DEMO_VIDEOS = [
    {
      key: "v1",
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_gadget-store-179/artifacts/j7rmbriz_1.mp4",
      label: lang === "gu" ? "ડેમો 1" : "Product demo",
    },
    {
      key: "v2",
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_gadget-store-179/artifacts/oz7lo0io_2.mp4",
      label: lang === "gu" ? "ડેમો 2" : "In real kitchen",
    },
    {
      key: "v3",
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_gadget-store-179/artifacts/72mjt1eb_3.mp4",
      label: lang === "gu" ? "ડેમો 3" : "Airflow test",
    },
    {
      key: "v4",
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_gadget-store-179/artifacts/5a6zv4cc_4.mp4",
      label: lang === "gu" ? "ડેમો 4" : "Customer use",
    },
  ];

  return (
    <div className="font-body">
      {/* ============== HERO — autoplay video background, minimal thin text ============== */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-[#2C3E50]"
      >
        {/* Autoplay video background */}
        <div className="absolute inset-0">
          <video
            ref={heroVideoRef}
            data-testid="hero-video"
            src={HERO_VIDEO}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture
            className="w-full h-full object-cover pointer-events-none"
          />
          {/* Subtle overlay for text readability on the left */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10 sm:from-black/60 sm:via-black/25 sm:to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 min-h-[420px] sm:min-h-[520px] flex items-center">
          <div className="max-w-lg text-white">
            <Badge
              data-testid="hero-eyebrow"
              className="bg-white/15 text-white border border-white/25 hover:bg-white/15 rounded-full px-3 py-1 mb-4 font-normal backdrop-blur"
            >
              {t("hero_eyebrow")}
            </Badge>
            <h1
              data-testid="hero-heading"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] tracking-tight text-white"
            >
              {t("hero_title")}
            </h1>
            <div className="mt-5 space-y-2 max-w-md">
              <p className="text-base sm:text-lg text-white/90 font-light tracking-tight leading-relaxed">
                {t("hero_sub_1")}
              </p>
              <p className="text-base sm:text-lg text-[#F39C12] font-medium tracking-tight">
                {t("hero_sub_2")}
              </p>
              <p className="text-sm sm:text-base text-white/80 font-light leading-relaxed">
                {t("hero_sub_3")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PRODUCT ============== */}
      <section id="product" className="py-10 sm:py-14 bg-[#FBF7F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-6 lg:gap-12">
          <div className="lg:col-span-6">
            <Slideshow
              testId="product-slideshow"
              slides={PRODUCT_PHOTOS}
              renderSlide={(photo, idx) => (
                <Placeholder
                  testId={`product-photo-${idx}`}
                  aspect="aspect-square"
                  image={photo.url}
                  imageAlt={photo.alt}
                  className="bg-white !rounded-none"
                />
              )}
            />
          </div>

          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2C3E50]">
              Gruham {t("product_name")}
            </h2>

            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                data-testid="product-rating-btn"
                onClick={scrollToReviews}
                className="flex items-center gap-1 hover:opacity-80 transition"
                aria-label="Read reviews"
              >
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < Math.round(rating)
                        ? "fill-[#F39C12] text-[#F39C12]"
                        : "text-[#F39C12]/40"
                    }`}
                  />
                ))}
                <span className="ml-1 text-sm font-semibold text-[#2C3E50]">
                  {rating}
                </span>
              </button>
              <span className="text-sm text-[#2C3E50]/80">
                · {reviewsCount} reviews
              </span>
              <Badge
                data-testid="stock-status-badge"
                className={
                  outOfStock
                    ? "bg-[#D63031]/15 text-[#D63031] hover:bg-[#D63031]/15 border-0 rounded-full"
                    : "bg-[#27AE60]/15 text-[#27AE60] hover:bg-[#27AE60]/15 border-0 rounded-full"
                }
              >
                {outOfStock ? "Out of Stock" : t("in_stock")}
              </Badge>
            </div>

            <div className="mt-5 flex items-baseline gap-3 flex-wrap">
              <p
                data-testid="product-price"
                className="font-display text-4xl sm:text-5xl font-bold text-[#D63031]"
              >
                ₹{price.toLocaleString("en-IN")}
              </p>
              <p className="text-base text-[#7F8C8D] line-through">
                ₹{mrp.toLocaleString("en-IN")}
              </p>
              <span className="bg-[#27AE60]/15 text-[#27AE60] px-2 py-0.5 rounded text-xs font-bold">
                {discountPercent}% {t("off")}
              </span>
            </div>
            <p className="text-xs text-[#7F8C8D] mt-1">
              {t("inclusive_of_tax")} · {t("free_shipping")}
            </p>

            {product?.stock !== undefined && product.stock > 0 && product.stock <= 20 && (
              <div
                data-testid="stock-badge"
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D63031]/10 border border-[#D63031]/30"
              >
                <Flame className="w-4 h-4 text-[#D63031]" />
                <span className="text-sm font-semibold text-[#D63031]">
                  {lang === "gu"
                    ? `માત્ર ${product.stock} બાકી`
                    : `Only ${product.stock} left in stock`}
                </span>
              </div>
            )}

            <ul className="mt-6 space-y-2.5">
              {PRODUCT_FEATURES.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-[#2C3E50] text-sm sm:text-base"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#27AE60] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {outOfStock ? (
                <NotifyMeButton
                  variant="primary"
                  label="Notify me when available"
                />
              ) : (
                <Button
                  data-testid="add-to-cart-btn"
                  onClick={handleAddToCart}
                  className="cta-red rounded-full h-14 px-7 text-base font-semibold flex-1"
                >
                  {t("add_to_cart")}
                </Button>
              )}
              <a
                data-testid="product-whatsapp-btn"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-6 bg-white border-2 border-[#F39C12] text-[#2C3E50] font-semibold hover:bg-[#FFF5E1] transition"
              >
                <MessageCircle className="w-5 h-5" />
                {t("ask_whatsapp")}
              </a>
            </div>

            {/* Technical Specifications collapsible */}
            <Accordion type="single" collapsible className="mt-5">
              <AccordionItem
                value="tech-spec"
                data-testid="tech-spec-accordion"
                className="bg-white border border-[#F39C12]/25 rounded-2xl px-5"
              >
                <AccordionTrigger className="text-left font-display font-semibold text-[#2C3E50] hover:no-underline py-4">
                  Technical Specification
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  <table className="w-full text-sm" data-testid="tech-spec-table">
                    <tbody>
                      {TECH_SPECS.map((s) => (
                        <tr
                          key={s.label}
                          className="border-t border-[#F39C12]/15"
                        >
                          <td className="py-2.5 pr-4 text-[#7F8C8D] font-medium">
                            {s.label}
                          </td>
                          <td className="py-2.5 text-[#2C3E50] font-semibold text-right">
                            {s.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* ============== SEE IT IN ACTION — horizontal video carousel ============== */}
      <section id="video" className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-2">
                {t("see_action")}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                {t("video_section_title")}
              </h2>
            </div>
            <button
              data-testid="read-reviews-btn"
              onClick={scrollToReviews}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#FFF5E1] border border-[#F39C12]/40 text-[#D63031] font-semibold text-sm hover:bg-[#FFEBC7] transition"
            >
              <Star className="w-4 h-4 fill-[#F39C12] text-[#F39C12]" />
              {t("read_reviews_44")}
            </button>
          </div>

          <div className="relative px-2 sm:px-4 max-w-3xl mx-auto">
            <HorizontalCarousel
              testId="video-carousel"
              itemMinWidth="min-w-[150px] xs:min-w-[170px] sm:min-w-[190px] max-w-[190px] sm:max-w-[210px]"
              autoScrollMs={3000}
            >
              {DEMO_VIDEOS.map((v, idx) => (
                <div
                  key={v.key}
                  data-testid={`demo-video-${idx}`}
                  className="w-full aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-md"
                >
                  <video
                    src={v.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-label={v.label}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </HorizontalCarousel>
            <p className="mt-3 text-center text-xs text-[#7F8C8D] sm:hidden">
              ← Swipe to see more demos →
            </p>
          </div>

          <InlineCTA testId="cta-after-videos" onAdd={handleAddToCart} />
        </div>
      </section>

      {/* ============== WHY THIS IS DIFFERENT — image-only comparison, auto-scroll ============== */}
      <section className="py-10 sm:py-14 bg-[#FFF5E1]/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-6">
            {t("why_diff_eyebrow")}
          </p>

          <Slideshow
            testId="comparison-slideshow"
            slides={COMPARISON_IMAGES}
            autoPlayMs={4000}
            renderSlide={(image, idx) => (
              <div
                data-testid={`comparison-slide-${idx}`}
                className="bg-white border border-[#F39C12]/20 rounded-2xl overflow-hidden"
              >
                <img
                  src={image}
                  alt={`Comparison ${idx + 1}`}
                  className="w-full max-h-[560px] object-contain bg-white"
                />
              </div>
            )}
          />

          <InlineCTA testId="cta-after-comparison" onAdd={handleAddToCart} />
        </div>
      </section>

      {/* ============== FEATURES SUMMARY ============== */}
      <section className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-2">
            {t("features_eyebrow")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-8">
            {t("features_title")}
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {PRODUCT_FEATURES.map((f, idx) => {
              const Icon = FEATURE_ICONS[idx] || Wind;
              return (
                <div
                  key={idx}
                  data-testid={`feature-card-${idx}`}
                  className="bg-white border border-[#F39C12]/20 rounded-2xl p-5 lift-on-hover"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#FFF5E1] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#D63031]" strokeWidth={1.8} />
                  </div>
                  <p className="text-sm sm:text-base text-[#2C3E50] font-medium leading-snug">
                    {f}
                  </p>
                </div>
              );
            })}
          </div>

          <InlineCTA testId="cta-after-features" onAdd={handleAddToCart} />
        </div>
      </section>

      {/* ============== CUSTOMER REVIEWS ============== */}
      <section
        id="reviews"
        className="py-10 sm:py-14 bg-[#FBF7F0] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-2">
            {t("reviews_eyebrow")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-8">
            {t("reviews_title")}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {allReviews.slice(0, 5).map((tm, idx) => (
              <article
                key={idx}
                data-testid={`testimonial-${idx}`}
                className="bg-white border border-[#F39C12]/15 rounded-2xl p-6 lift-on-hover"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < tm.rating
                          ? "fill-[#F39C12] text-[#F39C12]"
                          : "text-[#F39C12]/25"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-[#2C3E50] text-base leading-relaxed">
                  "{tm.quote}"
                </p>
                <div className="mt-5 pt-4 border-t border-[#F39C12]/15">
                  <p className="font-display font-semibold text-[#2C3E50]">
                    {tm.name}
                  </p>
                  <p className="text-xs text-[#7F8C8D]">
                    {tm.location} · Verified buyer
                    {tm.created_at
                      ? ` · ${new Date(tm.created_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}`
                      : ""}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Link
              to="/reviews"
              data-testid="see-all-reviews-btn"
              className="inline-flex items-center gap-2 rounded-full px-7 h-12 bg-white border-2 border-[#D63031] text-[#D63031] font-semibold hover:bg-[#D63031] hover:text-white transition"
            >
              See all reviews ({allReviews.length})
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <InlineCTA testId="cta-after-reviews" onAdd={handleAddToCart} />
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-6">
            {lang === "gu" ? "વારંવાર પૂછાતા પ્રશ્નો" : "Honest answers"}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                data-testid={`faq-item-${idx}`}
                className="bg-white border border-[#F39C12]/20 rounded-2xl px-5"
              >
                <AccordionTrigger className="text-left font-display font-semibold text-[#2C3E50] hover:no-underline py-4">
                  {lang === "gu" ? f.qGu : f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#2C3E50]/85 leading-relaxed pb-5">
                  {lang === "gu" ? f.aGu : f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============== TRUST BADGES ============== */}
      <section className="py-10 sm:py-12 bg-[#27AE60]/8 border-y border-[#27AE60]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#27AE60] font-semibold mb-3 text-center">
            {t("trust_eyebrow")}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {TRUST_BADGES.map((b) => {
              const Icon = TRUST_ICONS[b.icon] || ShieldCheck;
              return (
                <div
                  key={b.key}
                  data-testid={`trust-${b.key}`}
                  className="bg-white border border-[#27AE60]/25 rounded-2xl p-5 text-center"
                >
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#27AE60]/12 flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-[#27AE60]" />
                  </div>
                  <p className="font-display font-semibold text-[#2C3E50]">
                    {lang === "gu" ? b.titleGu : b.title}
                  </p>
                  <p className="text-xs text-[#7F8C8D] mt-1">
                    {lang === "gu" ? b.bodyGu : b.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-12 sm:py-14 bg-[#D63031] text-white relative overflow-hidden">
        <div className="absolute inset-0 warm-grain opacity-10 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
            {t("cta_title_2")}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/90">
            {t("cta_lead")}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="final-cta-buy"
              onClick={handleAddToCart}
              className="cta-gold rounded-full h-14 px-7 text-base font-semibold"
            >
              {t("buy_at", { price: price.toLocaleString("en-IN") })}
            </Button>
            <a
              data-testid="final-cta-whatsapp"
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-7 border-2 border-white text-white font-semibold hover:bg-white/10 transition"
            >
              <MessageCircle className="w-5 h-5" />
              {t("chat_first")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
